/* eslint-disable react/jsx-pascal-case */
import { useContext, useEffect, useState } from "react";
import GeneralContext from "../../context/GeneralContext/GeneralContext";
import api from "../../services/api";
import { displayMessage } from "../../helpers/messages";
import SL_Button from "../../components/buttons/Button";
import { SL_Input } from "../../components/input/Input";
import { Form, Modal, Skeleton, Upload } from "antd";
import { SL_Select } from "../../components/selects/Select";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import host from "../../services/host";

// Opções de gênero
const GENDER_OPTIONS = [
	{ value: "male", label: displayMessage("MALE") },
	{ value: "female", label: displayMessage("FEMALE") },
];

// Opções de tipo de dieta
const TYPE_OPTIONS = [
	{ value: "weight_loss", label: displayMessage("WEIGHT_LOSS") },
	{ value: "muscle_gain", label: displayMessage("MUSCLE_GAIN") },
];

// Opções de quantidade de refeições
const MEALS_COUNT_OPTIONS = [
	{ value: 3, label: "3 refeições" },
	{ value: 4, label: "4 refeições" },
	{ value: 5, label: "5 refeições" },
	{ value: 6, label: "6 refeições" },
];

// Opções de intolerância alimentar
const INTOLERANCE_OPTIONS = [
	{ value: "none", label: "Nenhuma" },
	{ value: "lactose", label: "Lactose" },
	{ value: "gluten", label: "Glúten" },
	{ value: "vegetarian", label: "Vegetariano" },
];

export function DetailDietModal({ onCloseModal, dietId }) {
	const [isLoading, setIsLoading] = useState(true);
	const [diet, setDiet] = useState(null);
	const [fileList, setFileList] = useState([]);
	const [form] = Form.useForm();

	const { setShowLoading } = useContext(GeneralContext);

	useEffect(() => {
		loadDiet();
	}, [dietId]);

	async function loadDiet() {
		setIsLoading(true);
		try {
			const response = await api.diets.find(dietId);
			const dietData = response.data;
			setDiet(dietData);

			form.setFieldsValue({
				gender: dietData.gender,
				type: dietData.type,
				meals_count: dietData.meals_count,
				food_intolerance: dietData.food_intolerance,
				description: dietData.description,
				sequence: dietData.sequence ?? 1,
			});
		} catch (error) {
			console.error("Erro ao carregar dieta:", error);
		} finally {
			setIsLoading(false);
		}
	}

	async function onFinishForm(values) {
		try {
			setShowLoading(true);

			// Criação do FormData para envio multipart
			const formData = new FormData();
			if (values.description !== undefined) {
				formData.append("description", values.description || "");
			}
			if (values.sequence !== undefined && values.sequence !== "" && String(values.sequence) !== String(diet.sequence)) {
				formData.append("sequence", values.sequence);
			}

			// Se houver novo arquivo, adiciona ao formData
			if (fileList.length > 0 && fileList[0].originFileObj) {
				formData.append("file", fileList[0].originFileObj);
			}

			await api.diets.update(dietId, formData);
			await onCloseModal(true);
		} catch (error) {
			console.error("Erro ao atualizar dieta:", error);
		} finally {
			setShowLoading(false);
		}
	}

	async function handleDownload() {
		try {
			setShowLoading(true);
			// Chama a rota /url/:gender/:type/:meals/:food_intolerance para obter a URL de download
			// passando a sequência da dieta atual via query
			const response = await api.diets.getDownloadUrl(
				diet.gender,
				diet.type,
				diet.meals_count,
				diet.food_intolerance,
				diet.sequence ?? 1
			);

			// Abre a URL de download em nova aba
			const downloadUrl = response.data.downloadUrl
			window.open(downloadUrl, "_blank");
		} catch (error) {
			console.error("Erro ao obter URL de download:", error);
		} finally {
			setShowLoading(false);
		}
	}

	// Configuração do upload de arquivo
	const uploadProps = {
		beforeUpload: () => false, // Previne upload automático
		onChange: ({ fileList: newFileList }) => {
			// Permite apenas 1 arquivo
			setFileList(newFileList.slice(-1));
		},
		fileList,
		accept: ".pdf",
		maxCount: 1,
	};

	return (
		<Modal
			title={
				<div className="text-xl font-bold text-blue-700 mr-3">
					{displayMessage("VIEW_DIET")} - ID: {dietId}
				</div>
			}
			open={true}
			onCancel={() => onCloseModal(false)}
			centered={true}
			footer={
				<div className="flex flex-col ss:flex-row gap-2 justify-end">
					<SL_Button
						className="w-full ss:w-[150px] bg-gray-500 border-transparent"
						onClick={() => onCloseModal(false)}
					>
						{displayMessage("CLOSE")}
					</SL_Button>

					<SL_Button
						className="w-full ss:w-[150px] m-0 bg-blue-600"
						onClick={() => form.submit()}
					>
						{displayMessage("SAVE")}
					</SL_Button>
				</div>
			}
			maskClosable={false}
			width={600}
		>
			{isLoading ? (
				<Skeleton active />
			) : (
				<Form
					onFinish={onFinishForm}
					layout="vertical"
					form={form}
					className={`flex gap-2 flex-col`}
				>
					<div className="gap-4 flex w-full flex-col smb:flex-row">
						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("GENDER")}:
								</span>
							}
							name="gender"
							className={`w-full m-0`}
						>
							<SL_Select options={GENDER_OPTIONS} disabled />
						</Form.Item>

						<Form.Item
							label={
								<span className="text-blue-700 font-semibold">
									{displayMessage("DIET_TYPE")}:
								</span>
							}
							name="type"
							className={`w-full m-0`}
						>
							<SL_Select options={TYPE_OPTIONS} disabled />
						</Form.Item>
					</div>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								Intolerância:
							</span>
						}
						name="food_intolerance"
						className={`w-full m-0`}
					>
						<SL_Select options={INTOLERANCE_OPTIONS} disabled />
					</Form.Item>

				<Form.Item
					label={
						<span className="text-blue-700 font-semibold">
							{displayMessage("MEALS_COUNT")}:
						</span>
					}
					name="meals_count"
					className={`w-full m-0`}
				>
					<SL_Select options={MEALS_COUNT_OPTIONS} disabled />
				</Form.Item>

				<Form.Item
					label={
						<span className="text-blue-700 font-semibold">
							{displayMessage("SEQUENCE")}:
						</span>
					}
					name="sequence"
					className={`w-full m-0`}
					required
					rules={[
						{ required: true, message: displayMessage("SEQUENCE_MANDATORY") },
					]}
					tooltip={displayMessage("SEQUENCE_HELPER")}
				>
					<SL_Input type="number" className="text-blue-700" />
				</Form.Item>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("DESCRIPTION")}:
							</span>
						}
						name="description"
						className={`w-full m-0`}
					>
						<SL_Input
							textarea={true}
							rows={2}
							type="text"
							className="text-blue-700"
						/>
					</Form.Item>

					{/* Exibição do arquivo atual */}
					<div className="flex flex-col gap-2">
						<span className="text-blue-700 font-semibold">
							{displayMessage("CURRENT_FILE")}:
						</span>
						<div className="flex items-center gap-2 p-2 bg-gray-100 rounded">
							<span className="flex-1 text-gray-700">
								{diet?.file || "Nenhum arquivo"}
							</span>
							{diet?.file && (
								<SL_Button
									type="default"
									icon={<DownloadOutlined />}
									onClick={handleDownload}
								>
									{displayMessage("DOWNLOAD_PDF")}
								</SL_Button>
							)}
						</div>
					</div>

					{/* Upload de novo arquivo (substituição) */}
					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("REPLACE_PDF")}:
							</span>
						}
						className={`w-full m-0 mt-2`}
					>
						<Upload {...uploadProps}>
							<SL_Button icon={<UploadOutlined />} type="default">
								{displayMessage("DRAG_FILE_HERE")}
							</SL_Button>
						</Upload>
					</Form.Item>
				</Form>
			)}
		</Modal>
	);
}
